import dayjs from 'dayjs'
import { AdGroupBudgetChangeType, BudgetCapping, CostType, DailyBudgetType } from '../../../../../__generated__/graphql'
import { ADGROUP_FLIGHT_DATE_FORMAT } from '../adgroupFlightDateFormat'
import { computeTimeframeValues, DateFormat, Timeframe } from '../../../../../components/stats/filters/StatsDateRange'
import { BudgetHistoryChangeAuthor } from './budgetHistoryChangeAuthor'
import { AdGroupBudgetHistoryEntry } from './budgetHistoryTypes'

export type BudgetHistoryChartPoint = {
  dateKey: string
  label: string
  costValue: number | null
  costType: CostType | null
  dailyBudgetAmount: number | null
  dailyBudgetType: DailyBudgetType | null
  budgetCappingAmount: number | null
  budgetCappingType: BudgetCapping | null
  dailyBudget_Open: number | null
  dailyBudget_Fixed: number | null
  dailyBudget_Auto: number | null
  budgetCapping_Open: number | null
  budgetCapping_Monthly: number | null
  budgetCapping_Period: number | null
  budgetCapping_Overall: number | null
  /** Full-height transparent bar for hover/tooltip on days without series data */
  interactionBand: number | null
  /** Set only on points that are a change; a null user means the platform made it. */
  changeAuthor: BudgetHistoryChangeAuthor | null
}

export const BUDGET_HISTORY_Y_AXIS_DOMAIN: [number, 'auto'] = [0, 'auto']

export type BudgetHistoryFallbacks = {
  costValue: number | null
  costType: CostType | null | undefined
  dailyBudget: number | null
  budgetCapping: number | null
  dailyBudgetType: DailyBudgetType | null | undefined
  budgetCappingType: BudgetCapping | null | undefined
}

type BudgetHistorySeriesNumericKey =
  | 'dailyBudget_Open'
  | 'dailyBudget_Fixed'
  | 'dailyBudget_Auto'
  | 'budgetCapping_Open'
  | 'budgetCapping_Monthly'
  | 'budgetCapping_Period'
  | 'budgetCapping_Overall'

const DAILY_BUDGET_SERIES_KEY: Record<DailyBudgetType, BudgetHistorySeriesNumericKey> = {
  [DailyBudgetType.Open]: 'dailyBudget_Open',
  [DailyBudgetType.Fixed]: 'dailyBudget_Fixed',
  [DailyBudgetType.Auto]: 'dailyBudget_Auto',
}

const BUDGET_CAPPING_SERIES_KEY: Record<BudgetCapping, BudgetHistorySeriesNumericKey> = {
  [BudgetCapping.Open]: 'budgetCapping_Open',
  [BudgetCapping.Monthly]: 'budgetCapping_Monthly',
  [BudgetCapping.Period]: 'budgetCapping_Period',
  [BudgetCapping.Overall]: 'budgetCapping_Overall',
}

const OPEN_DAILY_BUDGET_KEY: BudgetHistorySeriesNumericKey = 'dailyBudget_Open'
const OPEN_BUDGET_CAPPING_KEY: BudgetHistorySeriesNumericKey = 'budgetCapping_Open'

const isOpenBudgetSeriesKey = (key: BudgetHistorySeriesNumericKey): boolean =>
  key === OPEN_DAILY_BUDGET_KEY || key === OPEN_BUDGET_CAPPING_KEY

const setSeriesValue = (point: BudgetHistoryChartPoint, key: BudgetHistorySeriesNumericKey, value: number): void => {
  point[key] = value
}

/** Max numeric budget on the chart (lines only — open and auto bars are applied separately at full height). */
const computeBudgetPlotMax = (points: BudgetHistoryChartPoint[]): number =>
  points.reduce((max, point) => {
    const candidates = [
      point.dailyBudgetAmount,
      point.budgetCappingAmount,
      point.dailyBudget_Fixed,
      point.budgetCapping_Monthly,
      point.budgetCapping_Period,
      point.budgetCapping_Overall,
    ].filter((value): value is number => value != null && Number.isFinite(value) && value > 0)
    return Math.max(max, ...candidates, 0)
  }, 0)

const applyOpenTypeFullHeightBars = (points: BudgetHistoryChartPoint[], fullHeight: number): void => {
  points.forEach((point) => {
    if (point.dailyBudgetType === DailyBudgetType.Open) {
      point.dailyBudget_Open = fullHeight
    }
    if (point.dailyBudgetType === DailyBudgetType.Auto) {
      point.dailyBudget_Auto = fullHeight
    }
    if (point.budgetCappingType === BudgetCapping.Open) {
      point.budgetCapping_Open = fullHeight
    }
  })
}

type BudgetHistoryLineKey = BudgetHistorySeriesNumericKey | 'costValue'

/**
 * Carries a line value over the points between two values of the same line, so it stays connected across empty
 * points. The gap is left when `breaksLine` holds for a point in between, e.g. another type of the group.
 */
const fillLineSeriesGaps = (
  points: BudgetHistoryChartPoint[],
  lineKeys: BudgetHistoryLineKey[],
  breaksLine: (point: BudgetHistoryChartPoint, key: BudgetHistoryLineKey) => boolean = () => false
): void => {
  lineKeys.forEach((key) => {
    let lastIndex: number | null = null
    points.forEach((point, index) => {
      if (point[key] != null) {
        if (lastIndex != null) {
          const carried = points[lastIndex]![key]
          for (let gapIndex = lastIndex + 1; gapIndex < index; gapIndex += 1) points[gapIndex]![key] = carried
        }
        lastIndex = index
        return
      }
      if (breaksLine(point, key)) lastIndex = null
    })
  })
}

/** At type changes, duplicate the Y value on line series keys so step lines stay connected with per-type colors. */
const connectBudgetTypeLineTransitions = <T extends DailyBudgetType | BudgetCapping>(
  points: BudgetHistoryChartPoint[],
  readType: (point: BudgetHistoryChartPoint) => T | null,
  seriesKeyForType: (type: T) => BudgetHistorySeriesNumericKey,
  bridgeY: (point: BudgetHistoryChartPoint, type: T) => number | null,
  isUnconnectedType: (type: T) => boolean = () => false
): void => {
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1]!
    const current = points[index]!

    const previousType = readType(previous)
    const currentType = readType(current)
    if (previousType == null || currentType == null || previousType === currentType) continue
    if (isUnconnectedType(previousType) || isUnconnectedType(currentType)) continue

    const y = bridgeY(current, currentType) ?? bridgeY(current, previousType) ?? bridgeY(previous, previousType)
    if (y == null || !Number.isFinite(y)) continue

    const previousKey = seriesKeyForType(previousType)
    const currentKey = seriesKeyForType(currentType)
    if (!isOpenBudgetSeriesKey(previousKey)) {
      setSeriesValue(current, previousKey, y)
    }
    if (!isOpenBudgetSeriesKey(currentKey)) {
      setSeriesValue(current, currentKey, y)
    }
  }
}

type BudgetHistoryState = Omit<AdGroupBudgetHistoryEntry, 'at' | 'changeType' | 'user'>

type RawChartPoint = {
  dateKey: string
  label: string
  state: BudgetHistoryState | null
  changeAuthor: BudgetHistoryChangeAuthor | null
}

const toState = ({
  at: _at,
  changeType: _changeType,
  user: _user,
  ...state
}: AdGroupBudgetHistoryEntry): BudgetHistoryState => state

const fallbackState = (fallbacks: BudgetHistoryFallbacks): BudgetHistoryState => ({
  costValue: fallbacks.costValue,
  costType: fallbacks.costType ?? null,
  dailyBudget: fallbacks.dailyBudget,
  dailyBudgetType: fallbacks.dailyBudgetType ?? null,
  budgetCapping: fallbacks.budgetCapping,
  budgetCappingType: fallbacks.budgetCappingType ?? null,
})

const sortChanges = (changes: AdGroupBudgetHistoryEntry[]): AdGroupBudgetHistoryEntry[] =>
  [...changes].sort((left, right) => dayjs(left.at as string).valueOf() - dayjs(right.at as string).valueOf())

const groupChangesByDay = (sortedChanges: AdGroupBudgetHistoryEntry[]): Map<string, AdGroupBudgetHistoryEntry[]> => {
  const changesByDay = new Map<string, AdGroupBudgetHistoryEntry[]>()
  sortedChanges.forEach((change) => {
    const dateKey = dayjs(change.at as string).format(DateFormat)
    changesByDay.set(dateKey, [...(changesByDay.get(dateKey) ?? []), change])
  })
  return changesByDay
}

/** State the period opens with when the ad group was created before it; not a change, so it has no author. */
const isInitialEntry = (change: AdGroupBudgetHistoryEntry): boolean =>
  change.changeType === AdGroupBudgetChangeType.Initial

export const findBudgetHistoryCreation = (
  changes: AdGroupBudgetHistoryEntry[]
): AdGroupBudgetHistoryEntry | undefined =>
  changes.find((change) => change.changeType === AdGroupBudgetChangeType.Create)

const formatChangePointLabel = (calendarDay: dayjs.Dayjs, at: unknown): string => {
  const dateLabel = calendarDay.format(ADGROUP_FLIGHT_DATE_FORMAT)
  const time = dayjs(at as string)
  return time.isValid() ? `${dateLabel} ${time.format('HH:mm')}` : dateLabel
}

const emptyPoint = (dateKey: string, label: string): BudgetHistoryChartPoint => ({
  dateKey,
  label,
  costValue: null,
  costType: null,
  dailyBudgetAmount: null,
  dailyBudgetType: null,
  budgetCappingAmount: null,
  budgetCappingType: null,
  dailyBudget_Open: null,
  dailyBudget_Fixed: null,
  dailyBudget_Auto: null,
  budgetCapping_Open: null,
  budgetCapping_Monthly: null,
  budgetCapping_Period: null,
  budgetCapping_Overall: null,
  interactionBand: null,
  changeAuthor: null,
})

/**
 * `changes` holds the complete state left by the last change of each hour, opened by an initial entry when the ad
 * group was created before the period; days without a change are omitted, so the last known state is carried over
 * them. Without any entry, the current ad group values apply to the whole period. When the ad group was created
 * during the timeframe, the series starts on its creation day.
 */
export const buildBudgetHistoryChartSeries = (
  changes: AdGroupBudgetHistoryEntry[],
  timeframe: Timeframe,
  fallbacks: BudgetHistoryFallbacks
): BudgetHistoryChartPoint[] => {
  const { startDate, endDate } = computeTimeframeValues()[timeframe]
  const timeframeStart = dayjs(startDate).startOf('day')
  const rangeEnd = dayjs(endDate)
  const creation = findBudgetHistoryCreation(changes)
  const creationDay = creation != null ? dayjs(creation.at as string).startOf('day') : null
  if (creationDay != null && creationDay.isAfter(rangeEnd)) return []
  const rangeStart = creationDay != null && creationDay.isAfter(timeframeStart) ? creationDay : timeframeStart
  const dayCount = rangeEnd.diff(rangeStart, 'day') + 1

  if (dayCount <= 0) return []

  const sortedChanges = sortChanges(changes)
  const changesByDay = groupChangesByDay(sortedChanges)
  const lastChangeBeforeRange = sortedChanges.filter((change) => dayjs(change.at as string).isBefore(rangeStart)).pop()
  let currentState: BudgetHistoryState | null =
    lastChangeBeforeRange != null
      ? toState(lastChangeBeforeRange)
      : changes.length === 0
        ? fallbackState(fallbacks)
        : null
  const rawPoints: RawChartPoint[] = []

  for (let offset = 0; offset < dayCount; offset += 1) {
    const day = rangeStart.add(offset, 'day')
    const dateKey = day.format(DateFormat)
    const dayChanges = changesByDay.get(dateKey) ?? []

    if (dayChanges.length === 0) {
      rawPoints.push({
        dateKey,
        label: day.format(ADGROUP_FLIGHT_DATE_FORMAT),
        state: currentState,
        changeAuthor: null,
      })
      continue
    }

    const multiChangeDay = dayChanges.length > 1
    dayChanges.forEach((change, changeIndex) => {
      currentState = toState(change)
      const initial = isInitialEntry(change)
      rawPoints.push({
        dateKey: multiChangeDay ? `${dateKey}#${changeIndex}` : dateKey,
        label: initial ? day.format(ADGROUP_FLIGHT_DATE_FORMAT) : formatChangePointLabel(day, change.at),
        state: currentState,
        changeAuthor: initial ? null : { user: change.user },
      })
    })
  }

  const today = dayjs().startOf('day')
  const todayHasChanges = (changesByDay.get(today.format(DateFormat)) ?? []).some((change) => !isInitialEntry(change))
  if (todayHasChanges && !rangeEnd.startOf('day').isBefore(today) && currentState != null) {
    const tomorrow = today.add(1, 'day')
    rawPoints.push({
      dateKey: tomorrow.format(DateFormat),
      label: tomorrow.format(ADGROUP_FLIGHT_DATE_FORMAT),
      state: currentState,
      changeAuthor: null,
    })
  }

  const maxOf = (read: (state: BudgetHistoryState) => number | null): number =>
    rawPoints.reduce((max, point) => {
      if (point.state == null) return max
      const value = read(point.state)
      return value != null && Number.isFinite(value) ? Math.max(max, value) : max
    }, 0)
  const dailyNumericMax = maxOf((state) => state.dailyBudget)
  const cappingNumericMax = maxOf((state) => state.budgetCapping)
  const interactionBandHeight = Math.max(
    dailyNumericMax,
    cappingNumericMax,
    maxOf((state) => state.costValue),
    1
  )

  const chartPoints = rawPoints.map((point) => {
    const chartPoint = emptyPoint(point.dateKey, point.label)
    chartPoint.interactionBand = interactionBandHeight
    chartPoint.changeAuthor = point.changeAuthor
    const { state } = point
    if (state == null) return chartPoint

    chartPoint.costValue = state.costValue
    chartPoint.costType = state.costType
    chartPoint.dailyBudgetAmount = state.dailyBudget
    chartPoint.dailyBudgetType = state.dailyBudgetType
    chartPoint.budgetCappingAmount = state.budgetCapping
    chartPoint.budgetCappingType = state.budgetCappingType

    if (state.dailyBudgetType === DailyBudgetType.Fixed) {
      chartPoint.dailyBudget_Fixed = state.dailyBudget
    }

    if (state.budgetCappingType === BudgetCapping.Monthly) {
      chartPoint.budgetCapping_Monthly = state.budgetCapping
    } else if (state.budgetCappingType === BudgetCapping.Period) {
      chartPoint.budgetCapping_Period = state.budgetCapping
    } else if (state.budgetCappingType === BudgetCapping.Overall) {
      chartPoint.budgetCapping_Overall = state.budgetCapping
    }

    return chartPoint
  })

  const budgetPlotMax = computeBudgetPlotMax(chartPoints)
  const openBarFullHeight = Math.max(
    budgetPlotMax,
    dailyNumericMax,
    cappingNumericMax,
    fallbacks.dailyBudget ?? 0,
    fallbacks.budgetCapping ?? 0,
    fallbacks.costValue ?? 0,
    1
  )

  connectBudgetTypeLineTransitions(
    chartPoints,
    (point) => point.budgetCappingType,
    (type) => BUDGET_CAPPING_SERIES_KEY[type],
    (point) => point.budgetCappingAmount,
    (type) => type === BudgetCapping.Open
  )

  fillLineSeriesGaps(chartPoints, ['costValue'])
  fillLineSeriesGaps(
    chartPoints,
    [DAILY_BUDGET_SERIES_KEY[DailyBudgetType.Fixed]],
    (point) => point.dailyBudgetType != null && point.dailyBudgetType !== DailyBudgetType.Fixed
  )
  fillLineSeriesGaps(
    chartPoints,
    [BudgetCapping.Monthly, BudgetCapping.Period, BudgetCapping.Overall].map((type) => BUDGET_CAPPING_SERIES_KEY[type]),
    (point, key) => point.budgetCappingType != null && BUDGET_CAPPING_SERIES_KEY[point.budgetCappingType] !== key
  )
  applyOpenTypeFullHeightBars(chartPoints, openBarFullHeight)

  return chartPoints
}

/** Top of the broken budget Y-axis display scale — open and auto bars span full plot height. */
export const BUDGET_BROKEN_AXIS_OPEN_BAR_DISPLAY = 1

/** Open and auto bars use the top of the broken-axis display scale (100% plot height). */
export const applyBrokenAxisOpenBudgetBars = (point: BudgetHistoryChartPoint): BudgetHistoryChartPoint => {
  const next = { ...point }
  if (point.dailyBudgetType === DailyBudgetType.Open) {
    next.dailyBudget_Open = BUDGET_BROKEN_AXIS_OPEN_BAR_DISPLAY
  }
  if (point.dailyBudgetType === DailyBudgetType.Auto) {
    next.dailyBudget_Auto = BUDGET_BROKEN_AXIS_OPEN_BAR_DISPLAY
  }
  if (point.budgetCappingType === BudgetCapping.Open) {
    next.budgetCapping_Open = BUDGET_BROKEN_AXIS_OPEN_BAR_DISPLAY
  }
  return next
}

/** @deprecated Use buildBudgetHistoryChartSeries — kept for tests migrating cost-only helpers */
export const buildCostValueDailySeries = (
  changes: AdGroupBudgetHistoryEntry[],
  timeframe: Timeframe,
  lastKnownCostValue?: number | null
): Array<{ dateKey: string; label: string; costValue: number | null }> =>
  buildBudgetHistoryChartSeries(changes, timeframe, {
    costValue: lastKnownCostValue ?? null,
    costType: null,
    dailyBudget: null,
    budgetCapping: null,
    dailyBudgetType: null,
    budgetCappingType: null,
  }).map(({ dateKey, label, costValue }) => ({ dateKey, label, costValue }))
